import { useMemo } from "react";

import useClientAccounts from "@fartherfinance/frontend/api/Dashboard/hooks/useClientAccounts";
import {
  ClientId,
  FartherAccountId,
  HookResult,
  RequestConfig,
  TradingGroupId,
} from "@fartherfinance/frontend/api/Types";

import { HypotheticalTradeGroup } from "../reducer/types";
import useFindPortfolioPlan from "@src/multiCustodian/components/Client/Portfolio/v2/PortfolioPlanList/useFindPortfolioPlan";

import { NO_TRADE_GROUP_ID } from "./constants";
import { combineAccountNames } from "./utils";

type AccountIdToName = Record<FartherAccountId, string>;

type Result = HookResult<HypotheticalTradeGroup[]>;

export default function useSelectedAccountsToTradeGroupsV2(
  clientId: ClientId | null,
  excludedVirtualAccountIds: FartherAccountId[],
  authConfig: RequestConfig | null
): Result {
  const accounts = useClientAccounts(clientId, authConfig);

  // Get the portfolio plans
  const portfolioPlans = useFindPortfolioPlan(clientId as ClientId);

  const result: Result = useMemo(() => {
    if (accounts.isLoading || portfolioPlans.isLoading) {
      return {
        data: undefined,
        isLoading: true,
        hasError: false,
      };
    }

    if (accounts.hasError || portfolioPlans.hasError) {
      let msg = "accounts";
      if (portfolioPlans.hasError) {
        msg = "portfolio plans";
      }

      return {
        data: undefined,
        isLoading: false,
        hasError: true,
        error: `Error retrieving client's ${msg}`,
      };
    }

    const budgetAccountIds = accounts.data.fartherAccounts.filter(
      (a) => !excludedVirtualAccountIds.some((ea) => ea === a.accountId)
    );

    const accountIdToName = budgetAccountIds.reduce((acc, cur) => {
      const account = accounts.data.fartherAccounts.find(
        (a) => a.accountId === cur.accountId
      );
      if (account === undefined) {
        throw new Error("Can't find excludedVirtualAccountId in accounts list");
      }

      return {
        ...acc,
        [cur.accountId]: account.accountDetails.displayName,
      };
    }, {} as AccountIdToName);

    // Group plans by accounts to create trade groups
    const tradeGroups: HypotheticalTradeGroup[] = [];

    // Add portfolio plans with their accounts as trade groups
    if (portfolioPlans.data) {
      portfolioPlans.data.forEach((plan) => {
        // Filter accounts to only include those in budgetAccountIds
        const filteredAccounts = plan.accounts.filter((accountId) =>
          budgetAccountIds.some((ba) => ba.accountId === accountId)
        );

        if (filteredAccounts.length > 0) {
          const accountNames = filteredAccounts.map((accountId) => {
            if (!accountIdToName[accountId]) {
              throw new Error(
                `account: ${accountId} is not in accountIdToName look-up`
              );
            }
            return accountIdToName[accountId];
          });

          tradeGroups.push({
            tradingGroupId: plan.planId as unknown as TradingGroupId,
            modelName: plan.name,
            accountsInPortfolio: combineAccountNames(accountNames),
            virtualAccountIds: filteredAccounts,
            hypotheticalModel: null,
            hypotheticalModelId: null,
            isDirectIndexed: plan.taxStatus === "Tax Exempt",
            isTaxExempt: plan.taxStatus === "Tax Exempt",
            hypotheticalPortfolioModelId: undefined,
          });
        }
      });
    }

    // Find accounts that are not in any portfolio plans
    const accountsInPlans = new Set<FartherAccountId>();
    portfolioPlans.data?.forEach((plan) => {
      plan.accounts.forEach((accountId) => {
        accountsInPlans.add(accountId);
      });
    });

    const accountsWithoutPlans = budgetAccountIds.filter(
      (account) => !accountsInPlans.has(account.accountId)
    );

    // Add accounts without plans as a separate trade group
    if (accountsWithoutPlans.length > 0) {
      const accountNamesWithoutPlans = accountsWithoutPlans.map((account) => {
        if (!accountIdToName[account.accountId]) {
          throw new Error(
            `account: ${account.accountId} is not in accountIdToName look-up`
          );
        }
        return accountIdToName[account.accountId];
      });

      tradeGroups.push({
        tradingGroupId: NO_TRADE_GROUP_ID as TradingGroupId,
        modelName: "No current model",
        accountsInPortfolio: combineAccountNames(accountNamesWithoutPlans),
        virtualAccountIds: accountsWithoutPlans.map((a) => a.accountId),
        hypotheticalModel: null,
        hypotheticalModelId: null,
        isDirectIndexed: false,
        isTaxExempt: false,
        hypotheticalPortfolioModelId: undefined,
      });
    }

    return {
      data: tradeGroups,
      isLoading: false,
      hasError: false,
    };
  }, [accounts, portfolioPlans, excludedVirtualAccountIds]);

  return result;
}
